import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { FloatingChatButton } from './chat/FloatingChatButton';
import { ChatPanel } from './chat/ChatPanel';
import { ScrollingBanner } from './announcements/ScrollingBanner';
import { ForcedAnnouncementPopup } from './announcements/ForcedAnnouncementPopup';
import { useLocation } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { MessageCircleOff, Menu, UserCheck, X } from 'lucide-react';
import { Button } from './ui/button';

export const DashboardLayout = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUnsubscribed, setShowUnsubscribed] = useState(false);
  const location = useLocation();
  const { isModuleEnabled, loading } = useSubscription();
  const { theme } = useTheme();
  const { isWalkingAs, walkAsUser, exitWalkAs, originalUser } = useAuth();
  
  // Use white logo for dark theme, dark logo for light theme
  const logoSrc = theme === 'dark' ? '/kineserve-logo-white.svg' : '/kineserve-logo.svg';
  
  // Check if chatbot module is enabled using module key directly
  const isChatbotEnabled = isModuleEnabled('chatbot');
  // Don't show floating button on the dedicated chat page
  const showFloatingButton = !location.pathname.includes('/support-chat');

  const handleChatClick = () => {
    if (loading) return; // Wait for subscription data to load
    
    if (isChatbotEnabled) {
      setIsChatOpen(true);
    } else {
      setShowUnsubscribed(true);
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="dashboard-layout">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Walk As Banner */}
        {isWalkingAs && (
          <div 
            className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between z-[100]"
            data-testid="walk-as-banner"
          >
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              <span className="text-sm font-medium">
                You are in Walk As mode - viewing as <strong>{walkAsUser?.full_name}</strong> ({walkAsUser?.role?.replace('_', ' ')})
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={exitWalkAs}
              className="h-7 px-2 text-amber-950 hover:bg-amber-600 hover:text-amber-950"
              data-testid="exit-walk-as"
            >
              <X className="h-4 w-4 mr-1" />
              Exit Walk As
            </Button>
          </div>
        )}
        
        {/* Mobile header with hamburger */}
        <div className="lg:hidden flex items-center gap-3 p-4 border-b border-border bg-card">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarOpen(true)}
            className="p-2"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img 
            src={logoSrc}
            alt="KineServe" 
            className="h-8 w-auto"
          />
        </div>
        <ScrollingBanner />
        <Topbar />
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-x-auto">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
        {/* Copyright Footer */}
        <footer className="py-3 px-6 border-t border-border bg-card/50">
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Grey Bull Technologies. All rights reserved.
          </p>
        </footer>
      </div>
      
      {/* Floating Chat Button - Always show, but check subscription on click */}
      {showFloatingButton && (
        <>
          <FloatingChatButton onClick={handleChatClick} />
          {isChatbotEnabled && (
            <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
          )}
        </>
      )}

      {/* Unsubscribed Modal */}
      {showUnsubscribed && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={() => setShowUnsubscribed(false)}>
          <div 
            className="bg-card border border-border rounded-lg p-8 max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                <MessageCircleOff className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold">Chatbot Not Available</h3>
              <p className="text-muted-foreground">
                The Amie Chatbot module is not included in your current subscription plan. 
                Please contact your administrator to enable this feature.
              </p>
              <Button 
                onClick={() => setShowUnsubscribed(false)}
                className="mt-4"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Forced Announcement Popup */}
      <ForcedAnnouncementPopup />
    </div>
  );
};